import vine from '@vinejs/vine'
import type { FieldContext, Infer } from '@vinejs/vine/types'
import { weekDays } from '../enums/week-days.js'
import {
  hasExactlyOneHome,
  isLogisticsArrivalDayOffset,
  LOGISTICS_ARRIVAL_DAY_OFFSETS,
  LOGISTICS_DELIVERY_MODES,
  LOGISTICS_DRIVER_DOCUMENT_KINDS,
  LOGISTICS_EMPLOYMENT_TYPES,
  LOGISTICS_LICENCE_CATEGORIES,
  LOGISTICS_SIMULATION_CREATE_KINDS,
  LOGISTICS_SIMULATION_KINDS,
  LOGISTICS_STORE_VEHICLE_TYPES,
  LOGISTICS_VEHICLE_TYPES,
  type LogisticsArrivalDayOffset,
  type LogisticsDriverDocumentStatus,
  type LogisticsHome,
  type LogisticsParameters,
  type LogisticsSimulationKind,
} from '../logistics.js'
import { timeField } from './collaborator.js'
import { maxLengthMessage } from './messages.js'

// Point de vérité unique de la validation logistique partagée backend/BO. Deep-import
// uniquement (`@gozoki/api-types/src/validation/logistics`) — JAMAIS ré-exporté par le barrel
// (`index.ts`). Même raison que `validation/collaborator.ts` : ce module importe `vine` (runtime),
// que Metro embarquerait dans le bundle Expo via le barrel, cassant le build EAS sur
// `node:dns/promises`. L'app RN n'a besoin que des types, pas de vine.

// Énumérations métier : définies sans vine dans `../logistics.ts` (embarquable par Metro), et
// ré-exportées ici pour que les schémas du backend n'aient qu'un seul module à importer.
export {
  LOGISTICS_DELIVERY_MODES,
  LOGISTICS_DRIVER_DOCUMENT_KINDS,
  LOGISTICS_EMPLOYMENT_TYPES,
  LOGISTICS_LICENCE_CATEGORIES,
  LOGISTICS_SIMULATION_CREATE_KINDS,
  LOGISTICS_SIMULATION_KINDS,
  LOGISTICS_STORE_VEHICLE_TYPES,
  LOGISTICS_VEHICLE_TYPES,
}

// Format horaire `HH:MM` : celui de `validation/collaborator.ts` (`TIME_REGEX`), pas une copie.
export { timeField }

const timeWindowField = () => vine.object({ start: timeField(), end: timeField() })

/** Plages d'un jour au plus : borne le JSON relu par le solveur et recopié dans chaque audit. */
export const LOGISTICS_MAX_WINDOWS_PER_DAY = 8

const dayWindowsField = (maxPerDay: number | undefined) => {
  const windows = vine.array(timeWindowField())
  return (maxPerDay === undefined ? windows : windows.maxLength(maxPerDay)).optional()
}

/** Messages de `weeklyWindowsField` plafonné à `LOGISTICS_MAX_WINDOWS_PER_DAY`, posé sous `field`. */
export const weeklyWindowsMessages = (field: string) =>
  Object.fromEntries(
    weekDays.map((day) => [
      `${field}.${day}.array.maxLength`,
      `${LOGISTICS_MAX_WINDOWS_PER_DAY} plages au plus par jour.`,
    ]),
  )

/**
 * Fenêtres horaires par jour de semaine : forme partagée par `logistics_hubs.opening_windows` et
 * `store_logistics_profiles.reception_windows` (type modèle `LogisticsWeeklyWindows`). Chaque
 * jour est optionnel (tableau de fenêtres), jamais fusionné ni additionné ici — la validation
 * s'arrête à la forme, l'exploitation des plages appartient au solveur. `maxPerDay` : plafond de
 * plages par jour (sans plafond par défaut).
 */
// Les 7 clés ci-dessous doivent rester synchronisées avec `weekDays` (`enums/week-days.ts`) : pas
// de boucle dynamique (la forme des propriétés Vine s'infère mal une fois construite par
// itération), donc une seule modification de `weekDays` à répercuter ici manuellement.
export const weeklyWindowsField = ({ maxPerDay }: { maxPerDay?: number } = {}) =>
  vine.object({
    monday: dayWindowsField(maxPerDay),
    tuesday: dayWindowsField(maxPerDay),
    wednesday: dayWindowsField(maxPerDay),
    thursday: dayWindowsField(maxPerDay),
    friday: dayWindowsField(maxPerDay),
    saturday: dayWindowsField(maxPerDay),
    sunday: dayWindowsField(maxPerDay),
  })

/** Plage dont la fermeture ne suit pas l'ouverture : le solveur ne peut pas la desservir. */
export const LOGISTICS_WINDOW_ORDER_MESSAGE = 'La fermeture doit suivre l’ouverture.'

/**
 * Plage desservable : la fermeture (`HH:MM`) suit l'ouverture, même jour. Pas de fermeture à
 * minuit (`00:00` précède toute ouverture) ni de plage de nuit : le solveur lit chaque jour à part
 * (`dayWindow`) ; un hub ouvert jusqu'à minuit se saisit 23:55.
 */
export const isOrderedWindow = (window: { start: string; end: string }) => window.start < window.end

/**
 * Chaque plage d'une semaine (`weeklyWindowsField`) ferme après avoir ouvert : le solveur calcule
 * sa durée (`sequence.ts`), une plage inversée ou vide rendrait le site injoignable. Sur l'objet
 * des fenêtres ; l'erreur est rapportée sur l'objet lui-même, la première plage fautive en méta
 * (`{ day, index }`).
 */
export const orderedWindowsRule = vine.createRule((value, _options, field: FieldContext) => {
  const windows = value as Partial<Record<string, { start: string; end: string }[]>> | null
  for (const [day, slots] of Object.entries(windows ?? {})) {
    const index = (slots ?? []).findIndex((slot) => !isOrderedWindow(slot))
    if (index !== -1) {
      field.report(LOGISTICS_WINDOW_ORDER_MESSAGE, 'logisticsWindowOrder', field, { day, index })
      return
    }
  }
})

/**
 * `logistics_vehicles_single_home` / `collaborators_logistics_role_home` (CHECK en base) :
 * rattachement à exactement un atelier OU un hub, jamais les deux, jamais ni l'un ni l'autre. À poser en `.use()` sur l'OBJET `{
 * homeCentralKitchenId, homeHubId }` (pas sur un champ individuel) pour porter cette règle côté
 * validation plutôt que de laisser un payload invalide atteindre le CHECK — une violation de CHECK
 * Postgres remonterait en erreur brute (500), pas en erreur de validation propre.
 *
 * Posée sur l'objet, la règle reçoit les DEUX champs déjà résolus (y compris `null`) : posée sur
 * un champ nullable individuel, elle ne s'exécuterait jamais quand CE champ vaut `null` (vérifié
 * empiriquement avant d'écrire cette règle — Vine n'exécute pas les règles `.use()` d'un champ dont
 * la valeur résolue est `null`/`undefined`, sauf `implicit: true`).
 *
 * Ici (pas de `.exists()` DB) plutôt que dans le backend : la partie DB-aware du champ `home`
 * (contrainte `.exists({ table: ... })`, qui exige le provider Lucid/Vine non disponible dans ce
 * package partagé) reste composée côté backend, qui applique cette règle sur son propre objet.
 */
const HOME_SUBJECT_LABELS = {
  vehicle: 'Le véhicule',
  driver: 'Le chauffeur',
  collaborator: 'Le collaborateur',
} as const

/** Refus de `exactlyOneHomeRule`, et sa règle : aussi rendus hors Vine (profil chauffeur d'une fiche). */
export const exactlyOneHomeMessage = (subject: keyof typeof HOME_SUBJECT_LABELS) =>
  `${HOME_SUBJECT_LABELS[subject]} doit être rattaché à exactement un atelier ou un hub, jamais les deux ni aucun des deux.`
export const EXACTLY_ONE_HOME_RULE = 'logisticsExactlyOneHome'

export const exactlyOneHomeRule = (subject: keyof typeof HOME_SUBJECT_LABELS) =>
  vine.createRule((value, _options, field: FieldContext) => {
    const home = value as LogisticsHome
    if (!hasExactlyOneHome(home)) {
      field.report(exactlyOneHomeMessage(subject), EXACTLY_ONE_HOME_RULE, field)
    }
  })

/**
 * Refus d'un rôle logistique (`chauffeur`, `preparateur-hub`, `responsable-hub`) sans rattachement
 * (CHECK `collaborators_logistics_role_home`), commun au back-office et à l'API.
 */
export const LOGISTICS_ROLE_HOME_REQUIRED_MESSAGE =
  'Un rôle logistique exige un rattachement : atelier ou hub.'

/**
 * Catégories de permis DÉCLARÉES sur un profil chauffeur : au moins une, sans doublon. C'est cette
 * liste que l'activation confronte aux pièces vérifiées.
 */
export const licenceCategoriesField = () =>
  vine.array(vine.enum(LOGISTICS_LICENCE_CATEGORIES)).distinct().minLength(1)

/**
 * Décisions possibles d'une vérification de pièce. `pending` n'en fait pas partie : c'est l'état
 * initial posé par l'upload, jamais une décision qu'un vérificateur reprend.
 */
export const LOGISTICS_LICENCE_DECISIONS = [
  'verified',
  'rejected',
] as const satisfies readonly LogisticsDriverDocumentStatus[]

export const storeVehicleTypesField = () =>
  vine
    .array(vine.enum(LOGISTICS_STORE_VEHICLE_TYPES))
    .distinct()
    .maxLength(LOGISTICS_STORE_VEHICLE_TYPES.length)

/** `store_logistics_profiles.delivery_days` : jours de livraison, sans doublon. */
export const LOGISTICS_DELIVERY_DAYS = weekDays

export const deliveryDaysField = () =>
  vine
    .array(vine.enum(LOGISTICS_DELIVERY_DAYS))
    .distinct()
    .maxLength(LOGISTICS_DELIVERY_DAYS.length)

// --- Simulation de tournées -----------------------------------------------------------

/**
 * `logistics_simulations.activity_days` (#903) : jours de DÉPART de la tournée, au moins un, sans
 * doublon, même format que `delivery_days`. Le premier dans l'ordre de la semaine fixe la date de
 * référence du calcul (`referenceDateOf`) ; vérifier la tournée sur chacun de ses jours relève d'une
 * carte à venir, pas d'une simple validation. Vine ne sait pas transformer un tableau :
 * l'écriture les range avec `sortWeekDays` (une seule forme stockée, pas de faux changement d'audit).
 */
export const activityDaysField = () => deliveryDaysField().minLength(1)

/** Refus d'une liste de jours d'activité vide (`activityDays.array.minLength`), serveur et back-office. */
export const LOGISTICS_ACTIVITY_DAYS_REQUIRED_MESSAGE = "Choisissez au moins un jour d'activité."

/** `logistics_simulations.arrival_day_offset` : arrivée le jour du départ (0) ou le lendemain (1). */
export const arrivalDayOffsetField = () => vine.enum(LOGISTICS_ARRIVAL_DAY_OFFSETS)

/**
 * `kind` d'une création : `last_mile` ou `approach`, jamais `full` (#902,
 * `LOGISTICS_SIMULATION_CREATE_KINDS`). Non lu par le moteur, mais contraint `hubIds`
 * (`logisticsHubIdsForKindError`).
 */
export const simulationKindField = () => vine.enum(LOGISTICS_SIMULATION_CREATE_KINDS)

/**
 * `kind` d'une mise à jour : tous les types, pour qu'une simulation `full` déjà enregistrée puisse
 * renvoyer son type inchangé ; un passage VERS `full` est refusé après lecture de l'existant
 * (`logisticsSimulationKindChangeError`).
 */
export const simulationStoredKindField = () => vine.enum(LOGISTICS_SIMULATION_KINDS)

/** Message du refus d'un type sur `kind` (inconnu, ou `full` à la création ou en changement de type). */
export const LOGISTICS_SIMULATION_KIND_MESSAGE =
  'Choisissez une tournée « dernier km » ou « acheminement » : une tournée n’est jamais les deux.'

/**
 * Changement de type d'une simulation existante : `full` n'est accepté que s'il est déjà son type
 * (#902). `null` quand le changement est permis, sinon le message d'erreur.
 */
export const logisticsSimulationKindChangeError = (
  next: LogisticsSimulationKind,
  current: LogisticsSimulationKind,
): string | null =>
  next === 'full' && current !== 'full' ? LOGISTICS_SIMULATION_KIND_MESSAGE : null

/**
 * `approach` et `full` (historique, #902) transitent par un hub ; `last_mile` livre en direct,
 * sans hub.
 */
export const logisticsKindRequiresHub = (kind: LogisticsSimulationKind): boolean =>
  kind !== 'last_mile'

/**
 * Périmètre de hubs attendu par type de tournée (ticket #847) : `last_mile` livre directement
 * depuis l'atelier (aucun hub) ; `approach` (et `full`, historique) transite par UN SEUL hub, celui qui
 * oriente le moteur (famille directe seule, ou via le hub). Fonction pure (comme
 * `logisticsDemandCoverageError`) plutôt que règle Vine seule : la création (`kind` optionnel,
 * défaut `last_mile`) et la mise à jour (fusion payload + existant) rejouent la même vérification
 * après résolution des valeurs par défaut, ce qu'une règle posée sur le seul payload ne verrait pas.
 *
 * Retourne `null` quand tout va bien, sinon le message d'erreur français.
 */
export const logisticsHubIdsForKindError = (
  kind: LogisticsSimulationKind,
  hubIds: number[],
): string | null => {
  if (!logisticsKindRequiresHub(kind)) {
    return hubIds.length === 0
      ? null
      : 'Une tournée « dernier km » ne comporte aucun hub : elle livre directement depuis l’atelier.'
  }

  return hubIds.length === 1 ? null : 'Une tournée « acheminement » exige exactement un hub.'
}

/**
 * Borne de `parameters.vehicleIds` à la relecture : une simulation antérieure à #901 peut porter
 * plusieurs camions. À l'écriture d'un camion, un seul (`LOGISTICS_SINGLE_VEHICLE_MESSAGE`).
 */
export const LOGISTICS_MAX_VEHICLE_IDS = 100

/**
 * Un seul camion par tournée (#901, 30/09) : plusieurs camions sur le même trajet font plusieurs
 * tournées. Messages communs à l'API et au BO (création, onglet Contraintes).
 */
export const LOGISTICS_VEHICLE_REQUIRED_MESSAGE = 'Choisissez le camion de la tournée.'
export const LOGISTICS_SINGLE_VEHICLE_MESSAGE =
  'Une tournée n’a qu’un seul camion : plusieurs camions sur le même trajet font plusieurs tournées.'

/**
 * Règle « un seul camion, requis » (#901) sur les camions écrits : à la création, et quand le camion
 * d'une simulation change. Commune à l'API et au BO. `null` quand il y a exactement un camion.
 */
export const logisticsVehicleIdsError = (
  vehicleIds: readonly number[] | undefined,
): string | null => {
  if (vehicleIds === undefined || vehicleIds.length === 0) return LOGISTICS_VEHICLE_REQUIRED_MESSAGE
  return vehicleIds.length > 1 ? LOGISTICS_SINGLE_VEHICLE_MESSAGE : null
}

/** Bornes des contraintes de trajet (#855) : au-delà, la saisie est une erreur, pas une limite. */
export const LOGISTICS_MAX_ROUTE_DURATION_SECONDS_LIMIT = 24 * 3600
export const LOGISTICS_MAX_ROUTE_DISTANCE_METERS_LIMIT = 5_000_000

/** Longueur maximale du nom d'une simulation, côté serveur comme côté BO. */
export const LOGISTICS_SIMULATION_NAME_MAX_LENGTH = 120

/**
 * Longueurs maximales des champs texte d'un camion, côté serveur comme côté BO (le nom interne
 * l'est aussi par sa colonne, `varchar(50)`).
 */
export const LOGISTICS_VEHICLE_PLATE_MAX_LENGTH = 50
export const LOGISTICS_VEHICLE_INTERNAL_NAME_MAX_LENGTH = 50
export const LOGISTICS_VEHICLE_BRAND_MODEL_MAX_LENGTH = 80

/**
 * Caractères qu'un texte saisi ne peut pas contenir : contrôle, format, privé, non attribué ou
 * surrogate isolé (catégorie Unicode C), et remplissages invisibles (U+115F, U+1160, U+3164,
 * U+FFA0, U+2800) — un texte doit s'afficher tel qu'il est enregistré et comparé.
 */
const INVISIBLE_CHARACTER_CLASS = String.raw`\p{C}\u115F\u1160\u3164\uFFA0\u2800`

/** Texte affichable (marque, modèle). */
export const LOGISTICS_VISIBLE_TEXT_PATTERN = new RegExp(`^[^${INVISIBLE_CHARACTER_CLASS}]*$`, 'u')

/** Nom interne d'un camion : texte affichable qui contient au moins une lettre ou un chiffre. */
export const LOGISTICS_VEHICLE_NAME_PATTERN = new RegExp(
  `^(?=.*[\\p{L}\\p{N}])[^${INVISIBLE_CHARACTER_CLASS}]*$`,
  'u',
)

/**
 * Borne haute d'un nombre saisi, même texte côté API et BO : `max` est la borne (fiche,
 * `numberFieldIssue`) ou le gabarit Vine `'{{ max }}'` (fournisseur de messages de l'API).
 */
export const logisticsAtMostMessage = (max: number | string): string => `Au plus ${max}.`

/** Messages des règles d'un camion, communs à l'API et à la fiche du BO. */
export const LOGISTICS_VEHICLE_MESSAGES = {
  plateRequired: "L'immatriculation est requise.",
  plateWithoutCharacter: 'La plaque doit contenir au moins une lettre ou un chiffre.',
  invalidInternalName:
    'Le nom interne doit contenir une lettre ou un chiffre, sans caractère invisible.',
  invisibleText: 'Caractère invisible ou de contrôle non accepté.',
  maxLength: maxLengthMessage,
}

/**
 * Longueurs maximales des champs d'un transporteur (`createCarrier`, `updateCarrier`, modale du
 * BO) ; l'email suit `EMAIL_MAX_LENGTH`.
 */
export const LOGISTICS_CARRIER_NAME_MAX_LENGTH = 120
export const LOGISTICS_CARRIER_CONTACT_NAME_MAX_LENGTH = 120
export const LOGISTICS_CARRIER_CONTACT_PHONE_MAX_LENGTH = 40

/** Longueur maximale du nom d'un hub (`createHub`, `updateHub`, formulaire du BO). */
export const LOGISTICS_HUB_NAME_MAX_LENGTH = 120

/** Plus grande valeur d'une colonne `integer` Postgres : borne haute des nombres saisis. */
export const LOGISTICS_INTEGER_MAX = 2_147_483_647

/** Longueur maximale d'une recherche libre (`q` de `GET /logistics/simulations`, onglets du BO). */
export const LOGISTICS_SEARCH_MAX_LENGTH = 120

/**
 * Surcharges de paramètres d'une simulation (`logistics_simulations.parameters`). Tous optionnels :
 * le service `parameters.ts` côté backend fusionne ce qui est fourni avec les défauts et rend la
 * liste des requis manquants — ce schéma valide la FORME, jamais la complétude. Les clés sont
 * vérifiées à la compilation contre `LogisticsParameters` (`ParametersFieldKeysInSync` ci-dessous).
 */
export const parametersField = () =>
  vine.object({
    deliveryDeadline: timeField().optional(),
    stopDurationSeconds: vine.number().withoutDecimals().positive().optional(),
    hubTransferDurationSeconds: vine.number().withoutDecimals().min(0).optional(),
    maxContinuousDrivingMinutes: vine.number().withoutDecimals().positive().optional(),
    mandatoryBreakMinutes: vine.number().withoutDecimals().min(0).optional(),
    // Vol d'oiseau → route : jamais un raccourci, donc ≥ 1.
    detourFactor: vine.number().min(1).max(5).optional(),
    palisPerPallet: vine.number().withoutDecimals().positive().optional(),
    palletWeightKg: vine.number().positive().optional(),
    paliWeightKg: vine.number().positive().optional(),
    fuelCostPerKmCents: vine
      .object({
        VL: vine.number().withoutDecimals().min(0).optional(),
        PL: vine.number().withoutDecimals().min(0).optional(),
        SL: vine.number().withoutDecimals().min(0).optional(),
      })
      .optional(),
    // Sans défaut, jamais requis : absent = pas de contrainte (ticket #855).
    maxRouteDurationSeconds: vine
      .number()
      .withoutDecimals()
      .positive()
      .max(LOGISTICS_MAX_ROUTE_DURATION_SECONDS_LIMIT)
      .optional(),
    maxRouteDistanceMeters: vine
      .number()
      .withoutDecimals()
      .positive()
      .max(LOGISTICS_MAX_ROUTE_DISTANCE_METERS_LIMIT)
      .optional(),
    // Le camion de la tournée (#901) : un tableau pour relire sans migration les simulations
    // antérieures (plusieurs camions, ou aucun = Automatique, ticket #855), que le PATCH d'un autre
    // paramètre renvoie tels quels. « Un seul, requis » (`logisticsVehicleIdsError`) est donc
    // appliqué par les contrôleurs : à la création, et quand le camion change. Bornée : le
    // moteur relit la liste en un seul `whereIn` (Postgres plafonne les paramètres liés).
    // `resolveVehicles` juge encore s'il est mobilisable.
    vehicleIds: vine
      .array(vine.number().withoutDecimals().positive())
      .distinct()
      .minLength(1)
      .maxLength(LOGISTICS_MAX_VEHICLE_IDS)
      .optional(),
  })

/**
 * Vine ignore les clés inconnues : un paramètre présent dans `LogisticsParameters` mais absent du
 * schéma serait silencieusement retiré des payloads. Cette assertion échoue à la compilation dès
 * que les deux ensembles de clés divergent.
 */
type ParametersFieldKeys = keyof Infer<ReturnType<typeof parametersField>>
type ParametersFieldKeysInSync = [ParametersFieldKeys] extends [keyof LogisticsParameters]
  ? [keyof LogisticsParameters] extends [ParametersFieldKeys]
    ? true
    : never
  : never
const parametersFieldKeysInSync: ParametersFieldKeysInSync = true
void parametersFieldKeysInSync

/**
 * Demande d'une boutique, exprimée en supports. Les deux seuls types prévus par le métier, aucune
 * évolution attendue (CR §4.3).
 */
export interface LogisticsDemandEntry {
  pallets: number
  palis: number
}

/**
 * `logistics_simulations.demand` : supports par boutique, clé = `storeId` en string (jsonb).
 * Palettes 800×1200 et palis 800×600, les deux seuls types prévus. Zéro accepté sur les deux : la
 * création d'une tournée ne fixe pas les volumes (ni le ticket #847 ni la maquette ne les demandent),
 * durée et péages se calculent sans eux.
 */
export const demandField = () =>
  vine.record(
    vine.object({
      pallets: vine.number().withoutDecimals().min(0),
      palis: vine.number().withoutDecimals().min(0),
    }),
  )

/**
 * `demand` doit couvrir EXACTEMENT `storeIds` : ni boutique oubliée, ni boutique en trop. Rendu en
 * fonction pure (et pas seulement en règle Vine) parce que la mise à jour partielle doit rejouer
 * la même vérification sur la fusion « payload + existant », avec le même message.
 *
 * Retourne `null` quand tout va bien, sinon le message d'erreur français.
 */
export const logisticsDemandCoverageError = (
  storeIds: number[],
  demand: Record<string, LogisticsDemandEntry>,
): string | null => {
  const expected = new Set(storeIds.map(String))
  const actual = new Set(Object.keys(demand))

  const missing = [...expected].filter((id) => !actual.has(id))
  const extra = [...actual].filter((id) => !expected.has(id))
  if (missing.length === 0 && extra.length === 0) return null

  const details = [
    missing.length > 0 ? `manquante(s) : ${missing.join(', ')}` : null,
    extra.length > 0 ? `en trop : ${extra.join(', ')}` : null,
  ]
    .filter((part) => part !== null)
    .join(' ; ')

  return `La demande doit couvrir exactement les boutiques du périmètre (${details}).`
}

/**
 * Vine n'a pas de type « map dont les clés sont contraintes par un autre champ », d'où cette règle
 * posée en `.use()` sur l'OBJET `{ storeIds, demand }` (pas sur un champ) : seule position où les
 * deux valeurs sont résolues ensemble.
 */
export const demandCoversStoreIdsRule = vine.createRule((value, _options, field: FieldContext) => {
  const input = value as { storeIds?: number[]; demand?: Record<string, LogisticsDemandEntry> }
  // Un `storeIds`/`demand` absent ou mal typé a déjà été signalé par son propre schéma : on ne
  // redouble pas l'erreur.
  if (!Array.isArray(input.storeIds) || typeof input.demand !== 'object') return
  if (input.demand === null) return

  const error = logisticsDemandCoverageError(input.storeIds, input.demand)
  if (error !== null) field.report(error, 'logisticsDemandCoversStoreIds', field)
})

/** Refus d'un retour qui ne suit pas le départ, pour une arrivée le jour du départ. */
export const LOGISTICS_SAME_DAY_RETURN_MESSAGE =
  "L'heure de départ doit être strictement antérieure à l'heure limite de retour. Pour une tournée qui passe minuit, choisissez une arrivée « Le lendemain »."

/** Refus d'un retour qui ne précède pas le départ au cadran, pour une arrivée le lendemain. */
export const LOGISTICS_NEXT_DAY_RETURN_MESSAGE =
  "Avec une arrivée « Le lendemain », la tournée passe minuit : l'heure limite de retour doit être strictement antérieure à l'heure de départ. Pour un retour le même jour, choisissez « Le jour du départ »."

/**
 * Ordre du départ et de l'heure limite de retour, selon le jour d'arrivée (#903) :
 * - arrivée le jour du départ (`0`) : départ strictement avant le retour ;
 * - arrivée le lendemain (`1`) : tournée de nuit. Le retour se lit le lendemain, et tombe donc
 *   strictement AVANT l'heure de départ au cadran : « Le lendemain » n'existe que pour une tournée
 *   qui passe minuit, et une tournée dure moins de 24 h (le moteur lit le trafic heure par heure
 *   sur un seul tour de cadran).
 *
 * Les deux valeurs étant au format `HH:MM` validé, la comparaison lexicographique suffit. Même
 * raison qu'au-dessus d'en faire une fonction pure : la mise à jour partielle rejoue la règle sur
 * la fusion « payload + existant ».
 */
export const logisticsDepartureBeforeReturnError = (
  departureTime: string,
  returnDeadline: string,
  arrivalDayOffset: LogisticsArrivalDayOffset,
): string | null => {
  if (arrivalDayOffset === 0) {
    return departureTime < returnDeadline ? null : LOGISTICS_SAME_DAY_RETURN_MESSAGE
  }
  return returnDeadline < departureTime ? null : LOGISTICS_NEXT_DAY_RETURN_MESSAGE
}

export const departureBeforeReturnRule = vine.createRule((value, _options, field: FieldContext) => {
  const input = value as {
    departureTime?: string
    returnDeadline?: string
    arrivalDayOffset?: unknown
  }
  if (typeof input.departureTime !== 'string' || typeof input.returnDeadline !== 'string') return
  // Absent = le jour du départ, comme à l'écriture ; une valeur inconnue a déjà été signalée par
  // son propre schéma (`arrivalDayOffsetField`).
  const arrivalDayOffset = input.arrivalDayOffset ?? 0
  if (!isLogisticsArrivalDayOffset(arrivalDayOffset)) return

  const error = logisticsDepartureBeforeReturnError(
    input.departureTime,
    input.returnDeadline,
    arrivalDayOffset,
  )
  if (error !== null) field.report(error, 'logisticsDepartureBeforeReturn', field)
})
